import { BUTTON_VARIANT, VButton } from '@/component/UI/VButton';
import { X } from '@/component/UI/asset';
import { CustomEvent } from '@/util';

import { INITIAL_Z_INDEX, VDialog, useVDialogHandle } from '../VDialog';
import { MODAL_SIZE, MODAL_TRIGGER, MODAL_WIDTH_BY_SIZE } from './constant';
import style from './style';
import type { VModalProps } from './type';

const VModal = (props: VModalProps) => {
  const {
    children,
    footer,
    initialZIndex,
    size = MODAL_SIZE.MEDIUM,
    title,
    ...dialogProps
  } = props;
  const { dialogRef, setVisibleWithTrigger } = useVDialogHandle();

  const onButtonClick = async <
    T extends MODAL_TRIGGER.CONFIRM | MODAL_TRIGGER.CANCEL,
  >(
    visible: boolean,
    trigger: T,
    handler?: (event: CustomEvent<T, null, any>) => Promise<void> | void,
  ) => {
    const event = new CustomEvent(trigger, null);

    await handler?.(event);

    setVisibleWithTrigger(event.defaultPrevented ? !visible : visible, trigger);
  };

  return (
    <VDialog
      ref={dialogRef}
      initialZIndex={initialZIndex ?? INITIAL_Z_INDEX.MODAL}
      {...dialogProps}
    >
      <div
        className={style.vModal}
        style={
          {
            '--modal-width': `${MODAL_WIDTH_BY_SIZE[size]}px`,
          } as React.CSSProperties
        }
      >
        {typeof title === 'function' ? (
          <div className={style.vModalHeader}>
            {title()}{' '}
            <button
              type="button"
              onClick={() => onButtonClick(false, MODAL_TRIGGER.CANCEL)}
            >
              <X className={style.vModalClose + ' stroke-2'} />
            </button>
          </div>
        ) : (
          typeof title === 'string' && (
            <div className={style.vModalHeader}>
              <span>{title}</span>
              <button
                type="button"
                onClick={() => onButtonClick(false, MODAL_TRIGGER.CANCEL)}
              >
                <X className={style.vModalClose + ' stroke-2'} />
              </button>
            </div>
          )
        )}

        {<div className={style.vModalBody}>{children}</div>}

        {footer && typeof footer === 'function' ? (
          <div className={style.vModalFooter}>{footer()}</div>
        ) : (
          footer &&
          typeof footer === 'object' && (
            <div className={style.vModalFooter}>
              {footer.cancelText && (
                <VButton
                  variant={footer.cancelVariant || BUTTON_VARIANT.SECONDARY}
                  size="md"
                  outlined
                  onClick={() =>
                    onButtonClick(false, MODAL_TRIGGER.CANCEL, footer.onCancel)
                  }
                  type="button"
                >
                  {footer.cancelText}
                </VButton>
              )}
              {footer.confirmText && (
                <VButton
                  variant={footer.confirmVariant || BUTTON_VARIANT.PRIMARY}
                  size="md"
                  onClick={() =>
                    onButtonClick(
                      false,
                      MODAL_TRIGGER.CONFIRM,
                      footer.onConfirm,
                    )
                  }
                  type="button"
                >
                  {footer.confirmText}
                </VButton>
              )}
            </div>
          )
        )}
      </div>
    </VDialog>
  );
};

export default VModal;
